// @flow
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { useModularUI } from "./useModularUI";

import { HTTP_METHODS, MODULARUI_STATUS } from "../constants";
import { useModularUIKey } from "./useModularUIKey";

import type { ModularUIModel, Href } from "../models";

/**
 * Configuration options for low-level modular UI requests.
 */
export type HookOptions = {
  key?: string,
  origin?: string,
  contextPath?: string,
  removeOnUnmount?: boolean,
  formdata?: any,
};

/**
 * Configuration options for the useModularUIBasic hook.
 * Allows for type enforcement, caching control, and reload behavior.
 */
export type UseModularUIBasicOptions<T: ModularUIModel> = {
  expectedModels?: Array<string>, // List of model types (strings) that are valid for this call
  targetModel?: Class<T> | Array<Class<T>>,
  forceTargetModel?: boolean,
  origin?: string,
  contextPath?: string,
  cache?: boolean,
  key?: string,
  isReload?: boolean,
  removeOnUnmount?: boolean,
  formdata?: any, // If present, the hook switches from GET to POST
};

/**
 * Internal helper to transform high-level hook options into a configuration
 * object compatible with the core useModularUI fetcher.
 * Logic:
 * 1. Defaults to GET method.
 * 2. If 'formdata' is present, it switches to POST and assigns 'data'.
 * 3. Forces 'isReload' if the useReload check returns true.
 */
const createUseModularUIOptions = <T: ModularUIModel>(
  options: UseModularUIBasicOptions<T>,
  href: string,
  mustReload: boolean,
): Object => {
  const baseOptions = {
    expectedModels: [],
    method: HTTP_METHODS.GET,
    data: undefined,
    ...options,
    isReload: mustReload || options.isReload,
  };

  if (options.formdata != null) {
    baseOptions.method = HTTP_METHODS.POST;
    baseOptions.data = options.formdata;
  }

  return baseOptions;
};

/**
 * Validates that the returned model type matches one of the expected model strings.
 * Logs a warning to the console if there is a mismatch (useful for debugging UI-backend contracts).
 */
const validateModel = (model: any, expectedModels: Array<string>) => {
  if (expectedModels.length > 0) {
    const isCorrectModel = expectedModels.includes(model.type);
    if (!isCorrectModel) {
      console.warn(model, "is not of instance", expectedModels);
    }
  }
};

/**
 * A simplified abstraction over useModularUI that includes automatic
 * model validation and smart-reload logic based on router state.
 *
 * @param {string} defaultKey - The default identifier for the model.
 * @param {string | Href} href - The endpoint to fetch.
 * @param {UseModularUIBasicOptions} options - Extended configuration options.
 */
export const useModularUIBasic = <T: ModularUIModel>(
  defaultKey: string,
  href: string | Href,
  options: UseModularUIBasicOptions<T> = (({}: any): UseModularUIBasicOptions<T>),
): T | null => {
  const memoizedHref = useMemo(() => href.toString(), [href]);

  // Use the key provided in options if available, otherwise fallback to the default
  const key = options.key ?? defaultKey;

  // Determine if we need to bypass cache based on navigation 'reload' signals
  const mustReload = useReload(key, memoizedHref);

  // Memoize options to prevent useModularUI from re-fetching on every parent render
  const modularUIOptions = useMemo(
    () => createUseModularUIOptions(options, memoizedHref, mustReload),
    [options, memoizedHref, mustReload],
  );

  const modularUI = useModularUI(key, href, modularUIOptions);

  const expectedModels = useMemo(
    () => options.expectedModels ?? [],
    [options.expectedModels],
  );

  // Extract and validate the model from the modularUI wrapper object
  return useMemo((): T | null => {
    if (modularUI?.model) {
      validateModel(modularUI.model, expectedModels);
      return modularUI.model;
    }
    return null;
  }, [expectedModels, modularUI]);
};

/**
 * Determines if a model should be reloaded based on the 'reload' timestamp
 * found in the React Router location state.
 * This is used for "force refresh" scenarios where a user navigates to a
 * page and expects fresh data (e.g., after a form submission elsewhere).
 * @param {string} modelKey - The model type key.
 * @param {string} url - The resource URL.
 * @returns {boolean} True if the store data is stale compared to the reload signal.
 */
export const useReload = (modelKey: string, url: string): boolean => {
  const location = useLocation();
  const key = useModularUIKey(modelKey, url);

  // Access the current entry in the Redux store
  const modelEntry = useSelector((state) => state.modularui[key]);

  if (modelEntry) {
    const reload = location.state?.reload || 0;

    // If a reload timestamp exists in the router state
    if (reload > 0) {
      const isFullyLoaded = modelEntry?.status === MODULARUI_STATUS.FINISHED;
      const lastModification = modelEntry?.lastModification ?? 0;

      // Trigger reload only if the model is already finished loading
      // AND the data we have is older than the requested reload time.
      if (isFullyLoaded && lastModification < reload) {
        return true;
      }
    }
  }
  return false;
};
